import { useMemo, useState } from 'react'
import { useRouter } from '@tanstack/react-router'

import { useDb } from '@/lib/dbContext'
import { useAuth } from '@/lib/auth'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'

async function refreshKioskPairedDevices(
  collection: { utils?: { refetch?: () => Promise<unknown> }; preload?: () => Promise<unknown> } | null | undefined,
) {
  if (collection?.utils?.refetch) {
    await collection.utils.refetch()
    return
  }

  await collection?.preload?.()
}

export function KioskPairingApproveScreen() {
  const router = useRouter()
  const db = useDb() as ReturnType<typeof useDb> & {
    pb?: { send: Function }
    kioskPairedDevicesCollection?: { utils?: { refetch?: () => Promise<unknown> }; preload?: () => Promise<unknown> }
  }
  const { user } = useAuth()
  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const kioskRows = useMemo(
    () => Array.from(db.kiosksCollection.state.values()) as Array<{ id: string; name: string; label: string; friendlyName?: string }>,
    [db.kiosksCollection.state],
  )

  const kiosk = kioskRows[0] ?? null

  const tabletHomeUrl = typeof window !== 'undefined'
    ? new URL('/kiosk', window.location.origin).toString()
    : '/kiosk'

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!db.pb || !kiosk) return

    setIsSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      await db.pb.send('/api/kiosk/pairing/approve', {
        method: 'POST',
        body: {
          kioskId: kiosk.id,
          code: code.trim().toUpperCase(),
        },
      })
      await refreshKioskPairedDevices(db.kioskPairedDevicesCollection)
      setMessage(`Tablet paired for ${kiosk.friendlyName || kiosk.label}.`)
      setCode('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to pair kiosk tablet.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <main className="mx-auto flex max-w-xl flex-col gap-6 px-4 py-12">
        <header className="space-y-3">
          <h1 className="text-3xl font-semibold tracking-tight">Pair kiosk tablet</h1>
          <p className="text-sm text-slate-300">
            Signed in as {user?.name ?? 'staff user'}.
          </p>
          <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 text-sm leading-6 text-slate-300">
            <p>Open `/kiosk` on the tablet, wait for the 6-character pairing code, then authorise it here.</p>
            <p className="mt-2">Enter that code here to authorise the device.</p>
          </div>
        </header>

        <form onSubmit={onSubmit} className="space-y-6 rounded-xl bg-slate-900 p-6 shadow-xl ring-1 ring-slate-800">
          <div className="space-y-3">
            <FormFieldLabel className="text-slate-200">Kiosk</FormFieldLabel>
            <div className="rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-slate-50">
              {kiosk ? (kiosk.friendlyName || kiosk.label) : 'No kiosk configured'}
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-950/80 p-3 text-xs text-slate-400">
              <p className="font-medium text-slate-300">Tablet home page:</p>
              <p className="mt-2 break-all font-mono text-cyan-300">{tabletHomeUrl}</p>
            </div>
          </div>

          <div className="space-y-3">
            <FormFieldLabel className="text-slate-200">6-character code</FormFieldLabel>
            <UnderlineInput
              name="code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              className="w-full text-center text-3xl tracking-[0.35em] text-slate-50"
              placeholder="ABC123"
              autoCapitalize="characters"
              autoCorrect="off"
              maxLength={6}
            />
          </div>

          {kiosk ? <p className="text-sm text-slate-400">Target kiosk: {kiosk.friendlyName || kiosk.label}</p> : null}
          {message ? <p className="text-sm text-emerald-300">{message}</p> : null}
          {error ? <p className="text-sm text-rose-300">{error}</p> : null}

          <button
            type="submit"
            disabled={isSubmitting || code.trim().length !== 6 || !kiosk}
            className="flex w-full items-center justify-center rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? 'Pairing…' : 'Pair tablet'}
          </button>

          <button
            type="button"
            className="w-full text-sm text-slate-400 underline underline-offset-4"
            onClick={() => router.navigate({ to: '/kiosk-control' })}
          >
            Back to kiosk control panel
          </button>
        </form>
      </main>
    </div>
  )
}
